import React, { useEffect, useState } from "react";
import { useRef } from "react";
import {
  Tabs,
  Tab,
  Card,
  Dropdown,
  Button,
  Modal,
  Form,
} from "react-bootstrap";
import { Formik, Field, Form as FormikForm, ErrorMessage } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import {
  createQuestions,
  deleteQuestions,
  getQuestions,
  updateQuestions,
  uploadPdf,
} from "../../services/Admin/adminServices";
import Spinner from "../components/Common/Spinner";
import Pagination from "../components/Pagination";
import { uploadFileToS3 } from "../../../lib/S3Bucket";

const isImageFile = (url) => {
  if (!url) return false;
  return !!url.match(/\.(png|jpe?g|webp|gif|bmp)(\?.*)?$/i);
};
const isVideoFile = (url) => {
  if (!url) return false;
  return !!url.match(/\.(mp4|webm|ogg|mov)(\?.*)?$/i);
};
const getAcceptedFormats = (type) => {
  switch (type) {
    case "image":
      return "image/png, image/jpeg, image/jpg, image/webp, image/gif, image/bmp";
    case "video":
      return "video/mp4, video/webm, video/ogg, video/quicktime"; 
    case "animation":
      return ".json, image/png, image/jpeg, image/jpg, image/webp, video/mp4, video/webm, video/ogg, video/quicktime";
    default:
      return "";
  }
};

const getReadableFormats = (type) => {
  switch (type) {
    case "image":
      return "PNG, JPEG, JPG, WEBP, GIF, BMP";
    case "video":
      return "MP4, WEBM, OGG, MOV";
    case "animation":
      return "JSON, PNG, JPEG, JPG, WEBP, MP4, WEBM, OGG, MOV";
    default:
      return "";
  }
};

const MediaManager = () => {
  const [activeTab, setActiveTab] = useState("text");
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const pdfInputRef = useRef(null);
  const [preview, setPreview] = useState({
    question: "",
    answer: "",
  });

  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);

  const [uploading, setUploading] = useState({
    question: false,
    answer: false,
  });

  const { id } = useParams();
  const [data, setData] = useState([]);
  const [totalItems, setTotalItems] = useState(0);

  const getQuestion = async () => {
    try {
      setLoading(true);
      const res = await getQuestions(id, activeTab, currentPage, itemsPerPage);
      const questions = res?.data?.data?.questions || [];
      setTotalItems(res?.data?.data?.total || 0);
      setData(questions);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getQuestion();
  }, [activeTab, currentPage]);

  useEffect(() => {
    setCurrentPage(0);
  }, [activeTab]);

  const svgDots = (
    <svg width="20px" height="20px" viewBox="0 0 24 24">
      <circle cx="12" cy="5" r="2" fill="#000" />
      <circle cx="12" cy="12" r="2" fill="#000" />
      <circle cx="12" cy="19" r="2" fill="#000" />
    </svg>
  );

  const handleClose = () => {
    setShowModal(false);
    setSelectedItem(null);
    setPreview({ question: "", answer: "" });
  };

  const handleDelete = async (itemId) => {
    try {
      await deleteQuestions(itemId);
      toast.success("Deleted successfully");
      getQuestion();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const handleFileUpload = async (e, fieldName, setFieldValue) => {
    const file = e.target.files[0];
    if (!file) return;

    // 5MB Size Validation
    const MAX_FILE_SIZE = 5 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      toast.error("File size must be less than 5MB");
      e.target.value = null;
      return;
    }

    try {
      setUploading((prev) => ({ ...prev, [fieldName]: true }));
      const key = `uploads/${Date.now()}-${file.name}`;
      const res = await uploadFileToS3(file, key);

      if (res) {
        setPreview((prev) => ({
          ...prev,
          [fieldName]: res,
        }));
        setFieldValue(fieldName, res);
      }
    } catch (error) {
      toast.error("File upload failed");
      setPreview((prev) => ({ ...prev, [fieldName]: "" }));
    } finally {
      setUploading((prev) => ({ ...prev, [fieldName]: false }));
    }
  };
  const handleStatus=async(id, currentStatus)=>{
  try{
      const payload={
      questionId:id,
      isVerified:!currentStatus
    }
   await updateQuestions(payload)
   getQuestion();
    toast.success("Status updated successfully");
  }
  catch(err){
    toast.error("Failed to update status"); 

  }}
  const handlePdfUpload = async (e) => {
  const file = e.target.files?.[0];

  if (!file) return;

  if (file.type !== "application/pdf") {
    toast.error("Please select a PDF");
    return;
  }

  const formData = new FormData();

  formData.append("file", file);
  

  try {
    setUploadingPdf(true);

    const res = await uploadPdf(id,formData);

    if (res?.data?.status === "pending") {
      toast.loading("Processing PDF...", {
        id: "pdf-loader",
      });

      // poll API here if backend supports it
    } else {
      toast.success("PDF uploaded successfully");
      getQuestion();
    }
  } catch (err) {
    toast.error("Upload failed");
  } finally {
    setUploadingPdf(false);

    e.target.value = "";
  }
};

  const validationSchema = Yup.object({
    question: Yup.string().required("Question is required"),
    answer: Yup.string().required("Answer is required"),
  });

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  return (
    <div>
      <h2 style={{ color: "#87C55F" }}>Questions</h2>

    <div className="d-flex justify-content-between mb-3">
  <div />

  <div className="d-flex gap-2">

    <Button
      variant="secondary"
      onClick={() => pdfInputRef.current?.click()}
      disabled={uploadingPdf}
    >
      {uploadingPdf ? "Uploading..." : "Upload PDF"}
    </Button>

    <Button
      style={{ backgroundColor: "#87C55F", border: "none" }}
      onClick={() => setShowModal(true)}
    >
      Add New
    </Button>
  </div>

  <input
    ref={pdfInputRef}
    type="file"
    accept=".pdf"
    hidden
    onChange={handlePdfUpload}
  />
</div>

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
        {["text", "video", "image", "animation"].map((tabType) => (
          <Tab key={tabType} eventKey={tabType} title={tabType.toUpperCase()}>
            <div className="row mt-3">
              {loading ? (
                <Spinner />
              ) : data?.length > 0 ? (
                data.map((item) => (
                  <div key={item._id} className="col-md-6 mb-3">
                    <Card className="shadow-sm">
                      <Card.Body
                        className="d-flex justify-content-between"
                        style={{ background: "#BBB3CA" }}
                      >
                        <div style={{ overflow: "hidden" }}>
                          <div className="mb-2">
                            <div className="mt-3">
  <strong className="text-black">Status:</strong>{" "}
  {item.isVerified ? (
    <span className="badge bg-success ms-2">Enabled</span>
  ) : (
    <span className="badge bg-warning text-white ms-2">
     Disabled
    </span>
  )}
</div>
                            <strong className="text-black">Question:</strong>
                            {item.type === "text" && <h5 className="text-dark">{item.question}</h5>}
                            {item.type === "image" && (
                              <img
                                src={item.question}
                                style={{
                                  maxWidth: "100%",
                                  height: "100px",
                                  objectFit: "contain",
                                }}
                                alt="Question"
                                className="d-block mt-1"
                              />
                            )}
                            {item.type === "video" && (
                              <video
                                height="200"
                                controls
                                className="d-block mt-1"
                              >
                                <source src={item.question} />
                              </video>
                            )}

                            {item.type === "animation" &&
                              (isImageFile(item.question) ? (
                                <img
                                  src={item.question}
                                  style={{
                                    maxWidth: "100%",
                                    height: "100px",
                                    objectFit: "contain",
                                  }}
                                  alt="Animation PNG"
                                  className="d-block mt-1"
                                />
                              ) : isVideoFile(item.question) ? (
                                <video
                                  height="200"
                                  controls
                                  className="d-block mt-1"
                                >
                                  <source src={item.question} />
                                </video>
                              ) : (
                                <span className="d-block mt-1 text-muted">
                                  JSON Animation File
                                </span>
                              ))}
                          </div>

                          <div>
                            <strong className="text-black">Answer:</strong>
                            {item.answerType === "text" && (
                              <p className="text-dark mb-0">{item.answer}</p>
                            )}
                            {item.answerType === "image" && (
                              <img
                                src={item.answer}
                                style={{
                                  maxWidth: "100%",
                                  height: "100px",
                                  objectFit: "contain",
                                }}
                                alt="Answer"
                                className="d-block mt-1"
                              />
                            )}
                            {item.answerType === "video" && (
                              <video
                                height="100"
                                controls
                                className="d-block mt-1"
                              >
                                <source src={item.answer} />
                              </video>
                            )}

                            {item.answerType === "animation" &&
                              (isImageFile(item.answer) ? (
                                <img
                                  src={item.answer}
                                  style={{
                                    maxWidth: "100%",
                                    height: "100px",
                                    objectFit: "contain",
                                  }}
                                  alt="Answer PNG"
                                  className="d-block mt-1"
                                />
                              ) : isVideoFile(item.answer) ? (
                                <video
                                  height="200"
                                  controls
                                  className="d-block mt-1"
                                >
                                  <source src={item.answer} />
                                </video>
                              ) : (
                                <span className="d-block mt-1 text-muted">
                                  JSON Animation File
                                </span>
                              ))}
                          </div>
                        </div>

                        <Dropdown align="end">
                          <Dropdown.Toggle variant="light" className="p-1">
                            {svgDots}
                          </Dropdown.Toggle>

                          <Dropdown.Menu align="end">
                            <Dropdown.Item
                              onClick={() => {
                                setSelectedItem(item);
                                setShowModal(true);
                                setPreview({
                                  question:
                                    item.type !== "text" ? item.question : "",
                                  answer:
                                    item.answerType !== "text"
                                      ? item.answer
                                      : "",
                                });
                              }}
                            >
                              Edit
                            </Dropdown.Item>

                            <Dropdown.Item
                              onClick={() => handleStatus(item._id, item.isVerified)}
                            >
                              {item.isVerified ? "Disable Question" : "Enable Question"}
                            </Dropdown.Item>

                            <Dropdown.Item
                              onClick={() => handleDelete(item._id)}
                            >
                              Delete
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </Card.Body>
                    </Card>
                  </div>
                ))
              ) : (
                <p>No {tabType} available</p>
              )}
            </div>
          </Tab>
        ))}
      </Tabs>

      {data?.length > 0 && (
        <div className="card-footer clearfix mt-3">
          <div className="d-flex justify-content-between align-items-center">
            <div className="dataTables_info" style={{ color: "#87C55F" }}>
              Showing {currentPage * itemsPerPage + 1} to{" "}
              {totalItems > (currentPage + 1) * itemsPerPage
                ? (currentPage + 1) * itemsPerPage
                : totalItems}{" "}
              of {totalItems} entries
            </div>
            <div className="d-flex align-items-center" style={{ gap: "10px" }}>
              <Pagination
                pageCount={totalPages}
                pageValue={currentPage}
                setPage={setCurrentPage}
              />
            </div>
          </div>
        </div>
      )}

      <Modal show={showModal} size="xl" onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ color: "#87C55F" }}>
            {selectedItem ? "Edit Item" : "Add New Item"}
          </Modal.Title>
        </Modal.Header>

        <Formik
          initialValues={{
            question: selectedItem?.question || "",
            answer: selectedItem?.answer || "",
            type: selectedItem?.type || activeTab,
            answerType: selectedItem?.answerType || "text",
            subCategoryId: id,
          }}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              if (selectedItem) {
                await updateQuestions({
                  ...values,
                  questionId: selectedItem._id,
                });
                toast.success("Updated successfully");
              } else {
                await createQuestions(values);
                toast.success("Added successfully");
              }
              getQuestion();
              handleClose();
            } catch (error) {
              toast.error("Something went wrong");
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ isSubmitting, setFieldValue, values }) => (
            <FormikForm>
              <Modal.Body>
                <div className="mb-4 p-3 border rounded">
                  <h5 style={{ color: "#87C55F" }}>Question Setup</h5>

                  <Form.Group className="mb-3">
                    <Form.Label>Question Type</Form.Label>
                    <Field
                      as="select"
                      name="type"
                      className="form-select"
                      style={{ borderColor: "#87C55F" }}
                      onChange={(e) => {
                        setFieldValue("type", e.target.value);
                        setFieldValue("question", "");
                        setPreview((prev) => ({ ...prev, question: "" }));
                      }}
                    >
                      <option value="text">Text</option>
                      <option value="image">Image</option>
                      <option value="video">Video</option>
                      <option value="animation">Animation</option>
                    </Field>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    {values.type === "text" ? (
                      <>
                        <Form.Label>Question (Text)</Form.Label>
                        <Field
                          name="question"
                          className="form-control"
                          placeholder="ex. What part of digestive system..."
                          style={{ borderColor: "#87C55F" }}
                        />
                      </>
                    ) : (
                      <>
                        <Form.Label>
                          Upload Question {values.type} (Max 5MB)
                        </Form.Label>
                        {uploading.question ? (
                          <div className="text-success small">Uploading...</div>
                        ) : (
                          <>
                            <input
                              type="file"
                              accept={getAcceptedFormats(values.type)}
                              className="form-control"
                              style={{ borderColor: "#87C55F" }}
                              onChange={(e) =>
                                handleFileUpload(e, "question", setFieldValue)
                              }
                            />
                            <div className="text-muted small mt-1">
                              <strong>Accepted formats:</strong>{" "}
                              {getReadableFormats(values.type)}
                            </div>
                          </>
                        )}
                      </>
                    )}
                    <ErrorMessage
                      name="question"
                      component="div"
                      className="text-danger mt-1"
                    />
                  </Form.Group>

                  {preview.question && values.type !== "text" && (
                    <div className="mt-3">
                      <p className="text-muted small mb-1">Preview:</p>
                      {(values.type === "image" ||
                        (values.type === "animation" &&
                          isImageFile(preview.question))) && (
                        <img
                          src={preview.question}
                          height="100"
                          style={{ objectFit: "contain" }}
                          alt="Question Preview"
                        />
                      )}
                      {(values.type === "video" ||
                        (values.type === "animation" &&
                          isVideoFile(preview.question))) && (
                        <video src={preview.question} height="100" controls />
                      )}
                      {values.type === "animation" &&
                        !isImageFile(preview.question) &&
                        !isVideoFile(preview.question) && (
                          <span className="badge bg-info text-dark">
                            JSON Animation Ready
                          </span>
                        )}
                    </div>
                  )}
                </div>

                <div className="mb-4 p-3 border rounded">
                  <h5 style={{ color: "#87C55F" }}>Answer Setup</h5>

                  <Form.Group className="mb-3">
                    <Form.Label>Answer Type</Form.Label>
                    <Field
                      as="select"
                      name="answerType"
                      className="form-select"
                      style={{ borderColor: "#87C55F" }}
                      onChange={(e) => {
                        setFieldValue("answerType", e.target.value);
                        setFieldValue("answer", "");
                        setPreview((prev) => ({ ...prev, answer: "" }));
                      }}
                    >
                      <option value="text">Text</option>
                      <option value="image">Image</option>
                      <option value="video">Video</option>
                      <option value="animation">Animation</option>
                    </Field>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    {values.answerType === "text" ? (
                      <>
                        <Form.Label>Answer (Text)</Form.Label>
                        <Field
                          as="textarea"
                          name="answer"
                          className="form-control"
                          style={{ borderColor: "#87C55F", height: "100px" }}
                          placeholder="Enter answer text content"
                        />
                      </>
                    ) : (
                      <>
                        <Form.Label>
                          Upload Answer {values.answerType} (Max 5MB)
                        </Form.Label>
                        {uploading.answer ? (
                          <div className="text-success small">Uploading...</div>
                        ) : (
                          <>
                            <input
                              type="file"
                              accept={getAcceptedFormats(values.answerType)}
                              className="form-control"
                              style={{ borderColor: "#87C55F" }}
                              onChange={(e) =>
                                handleFileUpload(e, "answer", setFieldValue)
                              }
                            />
                            <div className="text-muted small mt-1">
                              <strong>Accepted formats:</strong>{" "}
                              {getReadableFormats(values.answerType)}
                            </div>
                          </>
                        )}
                      </>
                    )}
                    <ErrorMessage
                      name="answer"
                      component="div"
                      className="text-danger mt-1"
                    />
                  </Form.Group>

                  {preview.answer && values.answerType !== "text" && (
                    <div className="mt-3">
                      <p className="text-muted small mb-1">Preview:</p>

                      {(values.answerType === "image" ||
                        (values.answerType === "animation" &&
                          isImageFile(preview.answer))) && (
                        <img
                          src={preview.answer}
                          height="100"
                          style={{ objectFit: "contain" }}
                          alt="Answer Preview"
                        />
                      )}
                      {(values.answerType === "video" ||
                        (values.answerType === "animation" &&
                          isVideoFile(preview.answer))) && (
                        <video src={preview.answer} height="100" controls />
                      )}
                      {values.answerType === "animation" &&
                        !isImageFile(preview.answer) &&
                        !isVideoFile(preview.answer) && (
                          <span className="badge bg-info text-dark">
                            JSON Animation Ready
                          </span>
                        )}
                    </div>
                  )}
                </div>
              </Modal.Body>

              <Modal.Footer>
                <Button
                  type="submit"
                  disabled={
                    isSubmitting || uploading.question || uploading.answer
                  }
                  style={{ backgroundColor: "#87C55F", border: "none" }}
                >
                  {uploading.question || uploading.answer
                    ? "Uploading file..."
                    : selectedItem
                      ? "Update"
                      : "Add"}
                </Button>
              </Modal.Footer>
            </FormikForm>
          )}
        </Formik>
      </Modal>
    </div>
  );
};

export default MediaManager;
